/** #84 — o controle declara o PRÓPRIO universo NA TELA, não só no payload.
 *
 *  Mesmo padrão do hint das aplicações do SaldoAtualCard: UMA linha no estado
 *  fechado, detalhe ao abrir. O que aparece fechado é só o que tem PESO de
 *  DECISÃO ("alguém escolheu tirar": clientes excluídos #66, cancelados Omie),
 *  com contagem e valor. O que é MECÂNICA (versionamento/superseded,
 *  intercompany) vai pro rodapé técnico dentro do painel — declarar tudo com
 *  o mesmo peso é o mesmo que não declarar: o R$200MM sumiria no ruído.
 *
 *  Substitui o `ClientesExclusaoNote` estático (nome fixo, sem número, 10px
 *  itálico — a declaração que ninguém lia). Aqui o número vem do backend
 *  (`medir_exclusao`, a cada request) e nunca é digitado na UI. */
import { useId, useState } from "react";
import { ChevronDown } from "lucide-react";
import type { CoberturaBloco, ExcluidaDeclarada, FatiaPrevisto } from "../../api/types";
import { fmtBRL } from "../../utils/formatters";

export interface CoberturaHintProps {
  cobertura: CoberturaBloco | null | undefined;
  /** "deste fluxo" / "deste calendário" / "desta curva" — o sujeito da frase. */
  sujeito: string;
}

function linhaDecisao(e: ExcluidaDeclarada, sujeito: string): string {
  const valor = e.valor !== null && e.valor !== undefined ? fmtBRL(e.valor, { abbreviate: true }) : null;
  const n = e.n !== null && e.n !== undefined ? e.n : null;
  const rotulo = e.o_que.includes("#66")
    ? "clientes excluídos"
    : e.o_que.replace(/\s*\(.*\)\s*$/, "");
  if (valor !== null && n !== null) {
    const unidade = e.o_que.includes("#66") ? "contratos" : "títulos";
    return `${valor} (${n} ${unidade}) fora ${sujeito} — ${rotulo}`;
  }
  return `fora ${sujeito} — ${rotulo}`;
}

// #96: rótulo curto por fatia (o MODO de verificação, não o nome técnico).
const ROTULO_FATIA: Record<string, string> = {
  monitorada: "monitorada",
  encontro_possivel: "encontro possível",
  so_agregado: "só-agregado",
  cedido: "cedido (não-verificável)",
  propria_sem_extrato: "conta nossa sem extrato",
  conta_nao_classificada: "conta não classificada",
  titulo_nao_emitido: "título ainda não emitido",
  cliente_sem_cadastro_omie: "cliente sem cadastro Omie",
  titulo_sem_conta: "título sem conta",
};

function pctDe(f: FatiaPrevisto, total: number): string {
  if (!total || f.valor === null || f.valor === undefined) return "—";
  return `${Math.round((100 * f.valor) / total)}%`;
}

function linhaFatias(fatias: FatiaPrevisto[]): string {
  const total = fatias.reduce((s, f) => s + (f.valor ?? 0), 0);
  return (
    "verificável do previsto: " +
    fatias.map((f) => `${pctDe(f, total)} ${ROTULO_FATIA[f.fatia] ?? f.fatia}`).join(" · ")
  );
}

export function CoberturaHint({ cobertura, sujeito }: CoberturaHintProps) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  if (!cobertura) {
    // payload antigo/deploy skew: DIZ que não declara, nunca finge universo cheio.
    return (
      <p className="text-[11px] text-amber-400/80 mt-2" data-testid="cobertura-hint-ausente">
        universo não declarado por esta resposta
      </p>
    );
  }
  const decisao = cobertura.excluidas.filter((e) => e.peso === "decisao");
  const mecanica = cobertura.excluidas.filter((e) => e.peso === "mecanica");
  // destaque: só decisões COM número (as sem número vão no painel)
  const destaque = decisao.filter((e) => (e.n ?? 0) > 0 || (e.valor ?? 0) > 0);

  return (
    <div className="mt-2" data-testid="cobertura-hint">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
        className="flex items-start gap-2 text-left w-full rounded focus:outline-none focus:ring-2 focus:ring-indra-turquesa/40"
        data-testid="cobertura-hint-toggle"
      >
        <div className="flex-1 space-y-0.5">
          {cobertura.fatias.length > 0 && (
            <div className="text-xs text-white/60" data-testid="cobertura-fatias-linha">
              {linhaFatias(cobertura.fatias)}
            </div>
          )}
          {destaque.length === 0 ? (
            <div className="text-xs text-white/60" data-testid="cobertura-hint-linha">
              nenhuma exclusão por decisão {sujeito} · detalhe ao abrir
            </div>
          ) : (
            destaque.map((e) => (
              <div key={e.o_que} className="text-xs text-white/60" data-testid="cobertura-hint-linha">
                {linhaDecisao(e, sujeito)}
              </div>
            ))
          )}
        </div>
        <ChevronDown
          size={14}
          className={
            "text-white/50 flex-shrink-0 mt-0.5 transition-transform duration-200 " +
            (open ? "rotate-180" : "")
          }
        />
      </button>
      <div
        id={panelId}
        className={
          "grid transition-all duration-200 ease-out " +
          (open ? "grid-rows-[1fr] opacity-100 mt-1" : "grid-rows-[0fr] opacity-0")
        }
      >
        <div className="overflow-hidden">
          <div className="pt-2 border-t border-white/10 space-y-2 text-[11px] text-white/55">
            <p data-testid="cobertura-universo">
              <span className="text-white/70">Universo:</span> {cobertura.universo}
              {cobertura.as_of ? ` · dados de ${new Date(cobertura.as_of).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })} BRT` : ""}
            </p>
            {decisao.length > 0 && (
              <ul className="space-y-1" data-testid="cobertura-decisoes">
                {decisao.map((e) => (
                  <li key={e.o_que}>
                    <span className="text-white/70">{e.o_que}</span>
                    {e.medida ? ` — ${e.medida}` : ""}
                    <span className="block text-white/40">{e.motivo}</span>
                  </li>
                ))}
              </ul>
            )}
            {cobertura.fatias.length > 0 && (
              <ul className="space-y-1" data-testid="cobertura-fatias">
                {cobertura.fatias.map((f) => (
                  <li key={f.fatia}>
                    <span className="text-white/70">{ROTULO_FATIA[f.fatia] ?? f.fatia}</span>
                    {f.valor !== null && f.valor !== undefined
                      ? ` — ${fmtBRL(f.valor, { abbreviate: true })} (${f.n ?? "?"} títulos)`
                      : ""}
                    {f.motivo ? <span className="block text-white/40">{f.motivo}</span> : null}
                  </li>
                ))}
              </ul>
            )}
            {cobertura.contas && (
              <p data-testid="cobertura-contas">
                <span className="text-white/70">Contas:</span>{" "}
                {cobertura.contas.monitoradas} de {cobertura.contas.omie_ativas} contas Omie ativas com
                extrato nosso
                {cobertura.contas.monitoradas_sem_dado > 0
                  ? ` · ${cobertura.contas.monitoradas_sem_dado} sem dado (fora da soma)`
                  : ""}
                {" · "}
                {cobertura.contas.cessionarias_conhecidas} contas cessionárias
                {cobertura.contas.cessionarias_codigos_omie &&
                cobertura.contas.cessionarias_codigos_omie !== cobertura.contas.cessionarias_conhecidas
                  ? ` (${cobertura.contas.cessionarias_codigos_omie} códigos Omie — código ≠ conta)`
                  : ""}
                {cobertura.contas.proprias_sem_extrato
                  ? ` · ${cobertura.contas.proprias_sem_extrato} nossas sem extrato`
                  : ""}
                {cobertura.contas.nao_classificadas.length > 0 ? (
                  <span className="block text-amber-400/80" data-testid="cobertura-contas-nao-classificadas">
                    ⚠ conta de recebimento NÃO classificada:{" "}
                    {cobertura.contas.nao_classificadas
                      .map((c) => `${c.descricao} (${c.n_titulos} títulos, ${fmtBRL(c.valor, { abbreviate: true })})`)
                      .join("; ")}
                  </span>
                ) : null}
              </p>
            )}
            {mecanica.length > 0 && (
              <p className="text-[10px] text-white/35 italic" data-testid="cobertura-mecanica">
                Rodapé técnico: {mecanica.map((e) => e.o_que).join("; ")}.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
